/**
 * VendorScorecard.js
 * Vendor performance scoring dashboard with detailed breakdowns,
 * manual rating, and colour-coded composite scores.
 */
import { useState, useEffect, useCallback } from 'react';
import { vendorScoresAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import {
  RefreshCw, Star, Truck, FileCheck, ShieldCheck, Award, ChevronRight,
  Loader2, Save, ArrowLeft, TrendingUp, TrendingDown, Minus, AlertTriangle,
} from 'lucide-react';

function scoreColor(score) {
  if (score === null || score === undefined) return { bg: 'rgba(107,114,128,0.08)', text: '#9CA3AF', label: 'N/A' };
  if (score >= 80) return { bg: 'rgba(16,185,129,0.08)', text: '#059669', label: 'Good' };
  if (score >= 60) return { bg: 'rgba(245,158,11,0.08)', text: '#D97706', label: 'Fair' };
  return { bg: 'rgba(239,68,68,0.08)', text: '#DC2626', label: 'Low' };
}

function scoreDot(score) {
  if (score === null || score === undefined) return '#D1D5DB';
  if (score >= 80) return '#059669';
  if (score >= 60) return '#D97706';
  return '#DC2626';
}

function MetricCell({ value, suffix = '%' }) {
  if (value === null || value === undefined) return <span className="text-xs" style={{ color: '#D1D5DB' }}>—</span>;
  const c = scoreColor(value);
  return (
    <span className="text-xs font-semibold tabular-nums" style={{ color: c.text }}>
      {value}{suffix}
    </span>
  );
}

export default function VendorScorecard() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [ratingDialog, setRatingDialog] = useState(false);
  const [ratingForm, setRatingForm] = useState({ manual_rating: null, notes: '' });
  const [savingRating, setSavingRating] = useState(false);

  const fetchScores = useCallback(async () => {
    try {
      const res = await vendorScoresAPI.list();
      setScores(res.data || []);
    } catch { toast.error('Failed to load scores'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchScores(); }, [fetchScores]);

  const handleRecalculateAll = async () => {
    setRecalculating(true);
    try {
      const res = await vendorScoresAPI.recalculateAll();
      toast.success(`${res.data.recalculated} vendors recalculated`);
      fetchScores();
    } catch { toast.error('Failed'); }
    setRecalculating(false);
  };

  const openDetail = (vendor) => {
    setSelectedVendor(vendor);
    setRatingForm({ manual_rating: vendor.manual_rating, notes: vendor.notes || '' });
  };

  const handleSaveRating = async () => {
    if (!selectedVendor) return;
    setSavingRating(true);
    try {
      const res = await vendorScoresAPI.updateRating(selectedVendor.vendor_name, ratingForm);
      toast.success('Rating saved');
      setSelectedVendor(res.data);
      fetchScores();
    } catch { toast.error('Failed'); }
    setSavingRating(false);
  };

  if (loading) return <div className="space-y-3"><Skeleton className="h-12 w-full" /><Skeleton className="h-64 w-full" /></div>;

  /* ============ DETAIL VIEW ============ */
  if (selectedVendor) {
    const v = selectedVendor;
    const sc = scoreColor(v.composite_score);
    return (
      <div className="space-y-4" data-testid="vendor-detail-view">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setSelectedVendor(null)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h2 className="text-lg font-semibold" style={{ color: 'var(--brand-navy)' }}>{v.vendor_name}</h2>
            <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
              Last calculated: {v.last_calculated ? new Date(v.last_calculated).toLocaleString('en-IN') : 'Never'}
            </p>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl" style={{ backgroundColor: sc.bg }}>
            <Award className="w-5 h-5" style={{ color: sc.text }} />
            <div className="text-right">
              <p className="text-xl font-bold tabular-nums" style={{ color: sc.text }}>
                {v.composite_score !== null ? v.composite_score : '—'}
              </p>
              <p className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: sc.text }}>{sc.label}</p>
            </div>
          </div>
        </div>

        {/* Metric Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <MetricCard icon={Truck} label="Delivery Reliability" value={v.delivery_reliability_pct}
            detail={`${v.avg_delivery_delay_days}d avg delay · ${v.total_line_items} items`} />
          <MetricCard icon={FileCheck} label="Invoice Accuracy" value={v.invoice_accuracy_pct}
            detail={`${v.total_invoices} invoices processed`} />
          <MetricCard icon={ShieldCheck} label="Quality Pass Rate" value={v.quality_pass_rate_pct}
            detail={`${v.total_qc_inspections} QC inspections`} />
          <MetricCard icon={Star} label="Manual Rating" value={v.manual_rating ? `${v.manual_rating}/5` : null}
            detail={`${v.total_pos} POs total`} isStar />
        </div>

        {/* Volume Stats */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm" style={{ color: 'var(--brand-navy)' }}>Volume Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--ink-muted)' }}>POs</p>
                <p className="text-lg font-bold tabular-nums" style={{ color: 'var(--brand-navy)' }}>{v.total_pos}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--ink-muted)' }}>Line Items</p>
                <p className="text-lg font-bold tabular-nums" style={{ color: 'var(--brand-navy)' }}>{v.total_line_items}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--ink-muted)' }}>Invoices</p>
                <p className="text-lg font-bold tabular-nums" style={{ color: 'var(--brand-navy)' }}>{v.total_invoices}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--ink-muted)' }}>QC Done</p>
                <p className="text-lg font-bold tabular-nums" style={{ color: 'var(--brand-navy)' }}>{v.total_qc_inspections}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Manual Rating + Notes (Admin only) */}
        {isAdmin && (
          <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="manual-rating-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm" style={{ color: 'var(--brand-navy)' }}>Manual Rating & Notes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs">Rating (1-5 stars)</Label>
                <div className="flex items-center gap-1 mt-1">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} onClick={() => setRatingForm(p => ({
                      ...p, manual_rating: p.manual_rating === n ? null : n
                    }))}
                      className="p-1 rounded transition-colors hover:bg-amber-50"
                      data-testid={`rating-star-${n}`}>
                      <Star className="w-6 h-6" strokeWidth={1.5}
                        fill={ratingForm.manual_rating >= n ? '#C5A55A' : 'none'}
                        style={{ color: ratingForm.manual_rating >= n ? '#C5A55A' : '#D1D5DB' }} />
                    </button>
                  ))}
                  {ratingForm.manual_rating && (
                    <span className="text-xs font-semibold ml-2" style={{ color: '#C5A55A' }}>
                      {ratingForm.manual_rating}/5
                    </span>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea value={ratingForm.notes} onChange={e => setRatingForm(p => ({ ...p, notes: e.target.value }))}
                  className="mt-1 h-20 text-xs" placeholder="Vendor relationship notes..."
                  data-testid="vendor-notes-input" />
              </div>
              <Button size="sm" className="gap-1 text-xs h-8" style={{ backgroundColor: 'var(--brand-navy)' }}
                onClick={handleSaveRating} disabled={savingRating} data-testid="save-vendor-rating">
                {savingRating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                Save Rating
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Formula */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardContent className="py-3">
            <p className="text-[10px] font-medium" style={{ color: 'var(--ink-muted)' }}>
              Composite = Delivery(35%) + Quality(30%) + Invoice Accuracy(25%) + Manual Rating(10%)
              {!v.manual_rating && <span className="ml-1 italic">(No manual rating — weights redistributed)</span>}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  /* ============ LIST VIEW ============ */
  return (
    <div className="space-y-4" data-testid="vendor-scorecard-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold" style={{ color: 'var(--brand-navy)' }}>Vendor Scorecard</h2>
          <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>{scores.length} vendors tracked</p>
        </div>
        {isAdmin && (
          <Button variant="outline" size="sm" className="gap-1.5 text-xs h-8 rounded-lg"
            onClick={handleRecalculateAll} disabled={recalculating} data-testid="recalculate-all-btn">
            <RefreshCw className={`w-3 h-3 ${recalculating ? 'animate-spin' : ''}`} />
            {recalculating ? 'Calculating...' : 'Recalculate All'}
          </Button>
        )}
      </div>

      {scores.length === 0 ? (
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardContent className="py-10 text-center">
            <Award className="w-10 h-10 mx-auto mb-2" style={{ color: 'rgba(27,42,74,0.12)' }} />
            <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>No vendor scores yet</p>
            <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>
              Scores will populate as POs are created and fabrics are received.
            </p>
            {isAdmin && (
              <Button size="sm" className="mt-3 gap-1.5 text-xs" style={{ backgroundColor: 'var(--brand-navy)' }}
                onClick={handleRecalculateAll} disabled={recalculating}>
                <RefreshCw className="w-3 h-3" /> Generate Initial Scores
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[10px]">Vendor</TableHead>
                <TableHead className="text-[10px] text-center">Composite</TableHead>
                <TableHead className="text-[10px] text-center">Delivery</TableHead>
                <TableHead className="text-[10px] text-center">Quality</TableHead>
                <TableHead className="text-[10px] text-center">Invoice</TableHead>
                <TableHead className="text-[10px] text-center">Rating</TableHead>
                <TableHead className="text-[10px] text-center">POs</TableHead>
                <TableHead className="text-[10px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {scores.map(v => {
                const sc = scoreColor(v.composite_score);
                return (
                  <TableRow key={v.vendor_name} className="cursor-pointer hover:bg-gray-50/70"
                    onClick={() => openDetail(v)} data-testid={`vendor-row-${v.vendor_name}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: scoreDot(v.composite_score) }} />
                        <span className="text-xs font-semibold" style={{ color: 'var(--brand-navy)' }}>{v.vendor_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className="text-[10px] font-bold px-2 py-0.5 rounded-md tabular-nums"
                        style={{ backgroundColor: sc.bg, color: sc.text }}>
                        {v.composite_score !== null ? v.composite_score : '—'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center"><MetricCell value={v.delivery_reliability_pct} /></TableCell>
                    <TableCell className="text-center"><MetricCell value={v.quality_pass_rate_pct} /></TableCell>
                    <TableCell className="text-center"><MetricCell value={v.invoice_accuracy_pct} /></TableCell>
                    <TableCell className="text-center">
                      {v.manual_rating ? (
                        <div className="flex items-center justify-center gap-0.5">
                          <Star className="w-3 h-3" fill="#C5A55A" style={{ color: '#C5A55A' }} />
                          <span className="text-xs font-semibold" style={{ color: '#C5A55A' }}>{v.manual_rating}</span>
                        </div>
                      ) : <span className="text-xs" style={{ color: '#D1D5DB' }}>—</span>}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-xs tabular-nums" style={{ color: 'var(--ink-muted)' }}>{v.total_pos}</span>
                    </TableCell>
                    <TableCell><ChevronRight className="w-3.5 h-3.5 opacity-30" /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

/* ========== Metric Card (detail view) ========== */
function MetricCard({ icon: Icon, label, value, detail, isStar }) {
  const displayVal = value === null || value === undefined ? null : value;
  const sc = isStar ? (
    displayVal ? { bg: 'rgba(197,165,90,0.08)', text: '#C5A55A' } : { bg: 'rgba(107,114,128,0.06)', text: '#9CA3AF' }
  ) : scoreColor(typeof displayVal === 'number' ? displayVal : null);

  return (
    <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: sc.bg }}>
            <Icon className="w-4 h-4" style={{ color: sc.text }} />
          </div>
          <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--ink-muted)' }}>{label}</p>
        </div>
        <p className="text-2xl font-bold tabular-nums" style={{ color: sc.text }}>
          {displayVal !== null ? (isStar ? displayVal : `${displayVal}%`) : '—'}
        </p>
        <p className="text-[9px] mt-0.5" style={{ color: 'var(--ink-muted)' }}>{detail}</p>
      </CardContent>
    </Card>
  );
}

/* ========== Inline Vendor Score Badge (for use in fabric lists) ========== */
export function VendorScoreBadge({ vendorName, clickable = false }) {
  const [score, setScore] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!vendorName) return;
    vendorScoresAPI.badge(vendorName).then(r => {
      setScore(r.data);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [vendorName]);

  if (!loaded || !score || score.composite_score === null) return null;

  const dotColor = scoreDot(score.composite_score);
  const tooltipText = [
    score.delivery_reliability_pct !== null ? `Delivery: ${score.delivery_reliability_pct}%` : null,
    score.quality_pass_rate_pct !== null ? `Quality: ${score.quality_pass_rate_pct}%` : null,
    score.invoice_accuracy_pct !== null ? `Accuracy: ${score.invoice_accuracy_pct}%` : null,
  ].filter(Boolean).join(' | ');

  const content = (
    <span className={`inline-flex items-center gap-1 ${clickable ? 'cursor-pointer hover:opacity-80' : 'cursor-help'}`}
      title={tooltipText || 'No data'}
      data-testid={`vendor-score-badge-${vendorName}`}>
      <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: dotColor }} />
      <span className="text-[10px] font-semibold tabular-nums" style={{ color: dotColor }}>
        {score.composite_score}
      </span>
    </span>
  );

  if (clickable) {
    return <a href="/app/purchase-orders" onClick={e => { e.stopPropagation(); }}>{content}</a>;
  }
  return content;
}

/* ========== PO Vendor Warning (for use in PO detail page) ========== */
export function VendorScoreWarning({ vendorName }) {
  const [score, setScore] = useState(null);

  useEffect(() => {
    if (!vendorName) return;
    vendorScoresAPI.badge(vendorName).then(r => setScore(r.data)).catch(() => {});
  }, [vendorName]);

  if (!score || score.composite_score === null) return null;

  const sc = scoreColor(score.composite_score);

  if (score.composite_score >= 60) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: sc.bg }}
        data-testid="vendor-score-banner">
        <Award className="w-4 h-4" style={{ color: sc.text }} />
        <span className="text-xs font-medium" style={{ color: sc.text }}>
          Vendor Score: {score.composite_score} ({sc.label})
        </span>
      </div>
    );
  }

  return (
    <a href="/app/purchase-orders" className="block" data-testid="vendor-score-warning">
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg hover:opacity-90 transition-opacity" style={{ backgroundColor: 'rgba(239,68,68,0.06)', borderLeft: '3px solid #DC2626' }}>
        <AlertTriangle className="w-4 h-4" style={{ color: '#DC2626' }} />
        <div className="flex-1">
          <span className="text-xs font-semibold" style={{ color: '#DC2626' }}>
            Low-Rated Vendor (Score: {score.composite_score})
          </span>
          <p className="text-[10px]" style={{ color: '#6B7280' }}>
            Consider alternative sources for time-sensitive orders. Click for scorecard.
          </p>
        </div>
      </div>
    </a>
  );
}
